import { useRef, useState, useEffect } from 'react';
import { Stage, Layer, Image, Line as KonvaLine } from 'react-konva';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip } from '@mui/material';
import Swal from 'sweetalert2';

// Local Imports
import PolygonAnnotation from './PolygonAnnotation';
import PolygonIcon from '../../../../assets/images/polygonIcon.svg';
import RoiIcon from '../../../../assets/images/roiIcon.svg';
import UndoIcon from '../../../../assets/images/undoIocn.svg';
import HandCursorIcon from '../../../../assets/images/handCursorIcon.svg';
import RedoIcon from '../../../../assets/images/redoIcon.svg';
import {
  setAnnotationData,
  redo,
  undo,
  clearHistory
} from 'store/annotations/annotationSlice';
import { Trash2, RotateCcw, Copy } from 'lucide-react';
import { useGetFrameData, useReplicateFrameData } from 'api/sdk';
import { useAppSelector } from 'hooks';
import LabelSuggestionInput from './LabelSuggestionInput';
import LoadingBox from 'components/Loaders/LoadingBox/LoadingBox';

// Styles imports
import Styles from './AnnotationStyles.module.scss';

const MODES = {
  DRAW_POLYGON: 'DRAW_POLYGON',
  DRAW_RECTANGLE: 'DRAW_RECTANGLE',
  DRAG: 'DRAG'
};

const wrapperStyle = {
  display: 'flex',
  justifyContent: 'center',

  backgroundColor: 'white',
  position: 'relative',
  flexDirection: 'column'
};

const columnStyle = {
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: 'white'
};

const COLORS = [
  { stroke: '#FF6B6B', fill: 'rgba(255,107,107,0.5)' },
  { stroke: '#4ECDC4', fill: 'rgba(78,205,196,0.5)' },
  { stroke: '#45B7D1', fill: 'rgba(69,183,209,0.5)' },
  { stroke: '#96CEB4', fill: 'rgba(150,206,180,0.5)' },
  { stroke: '#D4A5A5', fill: 'rgba(212,165,165,0.5)' },
  { stroke: '#9B59B6', fill: 'rgba(155,89,182,0.5)' },
  { stroke: '#3498DB', fill: 'rgba(52,152,219,0.5)' },
  { stroke: '#1ABC9C', fill: 'rgba(26,188,156,0.5)' },
  { stroke: '#F1C40F', fill: 'rgba(241,196,15,0.5)' },
  { stroke: '#E67E22', fill: 'rgba(230,126,34,0.5)' }
];

const Canvas = ({
  getExistingLabels,
  setLoading,
  loading,
  handleReplicateFrameData
}) => {
  // State declarations
  const currentConfigId = useSelector(
    state => state.configuration.configuration
  );
  const annotationData = useSelector(state => state.annotation.annotationData);
  const currentCameraId = useSelector(state => state.configV2.currentCameraId);

  const frameIndex = useSelector(state => state.annotation.frameIndex);
  const [videoSource, setVideoSource] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const past = useSelector(state => state.annotation.history.past);
  const future = useSelector(state => state.annotation.history.future);
  const { data: frameData, refetch } = useGetFrameData(
    currentConfigId,
    currentCameraId,
    frameIndex
  );
  const [image, setImage] = useState(null);
  const [points, setPoints] = useState([]);
  const [size, setSize] = useState({});
  const [flattenedPoints, setFlattenedPoints] = useState();
  const [position, setPosition] = useState([0, 0]);
  const [isMouseOverPoint, setMouseOverPoint] = useState(false);
  const [isPolyComplete, setPolyComplete] = useState(false);
  const imageRef = useRef(null);
  const [activePolygonIndex, setActivePolygonIndex] = useState(-1);
  const [isDrawing, setIsDrawing] = useState(true);
  const [mode, setMode] = useState(MODES.DRAW_RECTANGLE);
  const [labelInput, setLabelInput] = useState({
    visible: false,
    x: 0,
    y: 0,
    value: ''
  });
  const [tempRectStartPoint, setTempRectStartPoint] = useState(null);
  const canvasWrapperRef = useRef(null);
  const [isDrawingInProgress, setIsDrawingInProgress] = useState(false);

  useEffect(() => {
    const newImage = new window.Image();
    if (frameData?.data) {
      const newVideoSource = `${process.env.REACT_APP_MINIO_URL}/${frameData.data.frame_data['frame_path']}`;
      setVideoSource(newVideoSource);
      setIsLoading(true);

      newImage.onload = () => {
        console.log('Image loaded successfully');
        setImage(newImage);
        imageRef.current = newImage;
        setSize({
          width: 960,
          height: 540
        });
        setIsLoading(false);
        setTimeout(() => {
          console.log('time is printing in the function');
          setLoading(false);
        }, 1500);
      };

      newImage.onerror = e => {
        console.error('Error loading image:', e);
        setImage(null);
        imageRef.current = null;
        setIsLoading(false);
      };

      newImage.src = newVideoSource;

      if (frameData?.data?.frame_data['bounding_box'] !== null) {
        const newAnnotations =
          frameData?.data?.frame_data['bounding_box']['frame_data'];
        if (
          frameData?.data?.frame_data['bounding_box']['frame_data'].length >= 5
        ) {
          setMode(MODES.DRAG);
        }
        dispatch(setAnnotationData(newAnnotations || []));
        dispatch(clearHistory());
      } else {
        dispatch(setAnnotationData([]));
        dispatch(clearHistory());
      }
    }

    // Cleanup function
    return () => {
      newImage.onload = null;
      newImage.onerror = null;
    };
  }, [videoSource, frameData]); // Remove frameIndex from dependencies

  // Handle frame index changes
  useEffect(() => {
    if (frameIndex !== undefined) {
      setImage(null); // Clear current image before fetching new one
      setMode(MODES.DRAW_POLYGON);
      setActivePolygonIndex(-1);
      refetch();
    }
  }, [frameIndex]);

  useEffect(() => {
    const handleKeyDown = e => {
      // Skip all keyboard shortcuts if label input is visible
      if (labelInput.visible) {
        // Only handle Escape key for canceling label input
        if (e.key === 'Escape') {
          setLabelInput({ visible: false, x: 0, y: 0, value: '' });
          setPoints([]);
          setPolyComplete(false);
          setIsDrawing(true);
          setTempRectStartPoint(null);
        }
        return;
      }

      // Rest of your existing keyboard shortcuts logic
      if (e.key === 'Escape') {
        if (isDrawing && points.length > 0) {
          setPoints([]);
          setPolyComplete(false);
          setIsDrawing(true);
          setTempRectStartPoint(null);
          setLabelInput({ visible: false, x: 0, y: 0, value: '' });
        }
        return;
      }

      // Undo - Ctrl+Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (!isDrawingInProgress && past.length > 0) {
          handleUndo();
        }
        return;
      }

      // Redo - Ctrl+Y or Ctrl+Shift+Z
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === 'y' || (e.key === 'z' && e.shiftKey))
      ) {
        e.preventDefault();
        if (!isDrawingInProgress && future.length > 0) {
          handleRedo();
        }
        return;
      }

      // Delete - Delete or Backspace key
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        mode === MODES.DRAG &&
        activePolygonIndex !== -1
      ) {
        e.preventDefault();
        handleDeletePolygon(activePolygonIndex);
        return;
      }

      // Toggle drag mode - D key
      if (e.key === 'd' || e.key === 'D') {
        handleModeChange(MODES.DRAG);
        return;
      }

      // Toggle between rectangle and polygon - R key
      if (e.key === 'r' || e.key === 'R') {
        handleModeChange(MODES.DRAW_RECTANGLE);
        return;
      }

      if (e.key === 'p' || e.key === 'P') {
        handleModeChange(MODES.DRAW_POLYGON);
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isDrawing,
    points.length,
    isDrawingInProgress,
    past.length,
    future.length,
    mode,
    activePolygonIndex,
    labelInput.visible
  ]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        canvasWrapperRef.current &&
        !canvasWrapperRef.current.contains(event.target) &&
        (isDrawing || labelInput.visible)
      ) {
        setPoints([]);
        setPolyComplete(false);
        setIsDrawing(true);
        setTempRectStartPoint(null);
        setLabelInput({ visible: false, x: 0, y: 0, value: '' });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDrawing, labelInput.visible]);

  useEffect(() => {
    if (stageRef.current) {
      const container = stageRef.current.container();
      container.style.cursor = mode === MODES.DRAG ? 'grab' : 'crosshair';
    }
  }, [mode]);

  useEffect(() => {
    if (
      isPolyComplete &&
      points.length >= (mode === MODES.DRAW_POLYGON ? 3 : 2)
    ) {
      const xs = points.map(p => p[0]);
      const ys = points.map(p => p[1]);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;

      setLabelInput({
        visible: true,
        x: centerX,
        y: centerY,
        value: ''
      });
    }
  }, [isPolyComplete, points, mode]);

  useEffect(() => {
    const flattenedPoints = points
      .concat(isPolyComplete ? [] : position)
      .reduce((a, b) => a.concat(b), []);
    setFlattenedPoints(flattenedPoints);
  }, [points, isPolyComplete, position]);

  // Refs
  const stageRef = useRef(null);

  const dispatch = useDispatch();

  // Utility functions
  const getMousePos = stage => {
    return [stage.getPointerPosition().x, stage.getPointerPosition().y];
  };

  const getRandomColor = () => {
    const randomIndex = Math.floor(Math.random() * COLORS.length);
    return COLORS[randomIndex];
  };

  const getCurrentLinePoints = () => {
    if (!points.length || isPolyComplete) return []; // Added isPolyComplete check
    let linePoints = points.reduce(
      (acc, point) => [...acc, point[0], point[1]],
      []
    );
    if (!isPolyComplete) {
      linePoints = [...linePoints, position[0], position[1]];
    }
    return linePoints;
  };

  // Action handlers
  const handleUndo = () => {
    if (past.length > 0) {
      dispatch(undo());
    }
  };

  const handleRedo = () => {
    if (future.length > 0) {
      dispatch(redo());
    }
  };

  const handleReset = () => {
    if (annotationData.length > 0) {
      dispatch(setAnnotationData([]));
      setPoints([]);
      setPolyComplete(false);
      setIsDrawing(true);
      setActivePolygonIndex(-1);
      setTempRectStartPoint(null);
      setMode(MODES.DRAW_POLYGON);
    }
  };

  const handleStartNewPolygon = () => {
    setIsDrawing(true);
    setActivePolygonIndex(-1);
    setPoints([]);
    setPolyComplete(false);
    setTempRectStartPoint(null);
  };

  const handleDeletePolygon = index => {
    if (index !== -1) {
      const newAnnotations = annotationData.filter((_, idx) => idx !== index);
      dispatch(setAnnotationData(newAnnotations));
    }
  };

  const handleModeChange = newMode => {
    if (mode === newMode) return; // Don't switch if it's the same mode

    setMode(newMode);
    setPoints([]);
    setPolyComplete(false);
    setTempRectStartPoint(null);
    setActivePolygonIndex(-1);
    setIsDrawing(true);

    if (stageRef.current) {
      const container = stageRef.current.container();
      container.style.cursor = newMode === MODES.DRAG ? 'grab' : 'crosshair';
    }
  };

  const handleLabelSubmit = labelData => {
    if (labelData.label) {
      const newPolygon = {
        points: [...points],
        id: Date.now(),
        label: labelData.label,
        labelId: labelData.id,
        type: mode,
        color: getRandomColor()
      };

      if (activePolygonIndex !== -1) {
        const updatedAnnotations = annotationData.map((poly, index) =>
          index === activePolygonIndex ? newPolygon : poly
        );
        dispatch(setAnnotationData(updatedAnnotations));
      } else {
        dispatch(setAnnotationData([...annotationData, newPolygon]));
      }
    }

    // Reset states
    setPoints([]);
    setFlattenedPoints([]);
    setPolyComplete(false);
    setActivePolygonIndex(-1);
    setIsDrawing(true);
    setLabelInput({ visible: false, x: 0, y: 0, value: '' });
    setTempRectStartPoint(null);
    setPosition([0, 0]);
    setIsDrawingInProgress(false);
  };

  const handleMouseDown = e => {
    if (mode === MODES.DRAG) return;
    if (!isDrawing || !stageRef.current) return;

    const stage = stageRef.current;
    const mousePos = getMousePos(stage);

    setIsDrawingInProgress(true);

    if (mode === MODES.DRAW_POLYGON) {
      if (isMouseOverPoint && points.length >= 3) {
        // Set poly complete first
        setPolyComplete(true);
        // Don't set isDrawing to false here

        // Then calculate and set label input position
        const xs = points.map(p => p[0]);
        const ys = points.map(p => p[1]);
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        setLabelInput({
          visible: true,
          x: centerX,
          y: centerY,
          value: ''
        });
      } else {
        setPoints(prev => [...prev, mousePos]);
      }
    } else if (mode === MODES.DRAW_RECTANGLE) {
      if (!tempRectStartPoint) {
        setTempRectStartPoint(mousePos);
        setPoints([mousePos]);
      } else {
        const [startX, startY] = tempRectStartPoint;
        const [endX, endY] = mousePos;

        const MINIMUM_DISTANCE = 5; // Minimum distance in pixels
        const distance = Math.sqrt(
          Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2)
        );

        if (distance < MINIMUM_DISTANCE) {
          // Invalid rectangle - points too close together
          Swal.fire({
            title: 'Invalid Rectangle',
            text: 'Please draw a rectangle with distinct points',
            icon: 'warning',
            width: 400,
            toast: true,
            position: 'bottom-end',
            showConfirmButton: false,
            timer: 3000
          });

          // Reset the drawing state
          setPoints([]);
          setTempRectStartPoint(null);
          setIsDrawingInProgress(false);
          return;
        }

        const rectanglePoints = [
          [startX, startY],
          [endX, startY],
          [endX, endY],
          [startX, endY]
        ];
        setPoints(rectanglePoints);
        setPolyComplete(true);
        // Don't set isDrawing to false here

        const xs = rectanglePoints.map(p => p[0]);
        const ys = rectanglePoints.map(p => p[1]);
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        setLabelInput({
          visible: true,
          x: centerX,
          y: centerY,
          value: ''
        });
      }
    }
  };

  const handleMouseMove = e => {
    if (
      mode === MODES.DRAG ||
      !isDrawing ||
      !stageRef.current ||
      isPolyComplete
    )
      return; // Added isPolyComplete check

    const stage = stageRef.current;
    const mousePos = getMousePos(stage);
    setPosition(mousePos);

    if (mode === MODES.DRAW_RECTANGLE && tempRectStartPoint) {
      const [startX, startY] = tempRectStartPoint;
      const [currentX, currentY] = mousePos;
      const rectanglePoints = [
        [startX, startY],
        [currentX, startY],
        [currentX, currentY],
        [startX, currentY]
      ];
      setPoints(rectanglePoints);
    }
  };

  const handlePointDragMove = data => {
    if (mode !== MODES.DRAG || activePolygonIndex === -1) return;

    const currentPolygon = annotationData[activePolygonIndex];
    const OFFSET = 10;

    const stage = stageRef.current;
    if (!stage) return;

    const x = Math.min(Math.max(data.point[0], OFFSET), size.width - OFFSET);
    const y = Math.min(Math.max(data.point[1], OFFSET), size.height - OFFSET);
    const constrainedPoint = [x, y];

    let newPoints;
    if (currentPolygon.type === MODES.DRAW_RECTANGLE) {
      // For rectangles, maintain shape while constraining all points
      newPoints = [...currentPolygon.points];
      const currentPoint = constrainedPoint;
      const index = data.index;

      // Update all points relative to the dragged point
      if (index % 2 === 0) {
        // Corner point
        const prevIndex = (index - 1 + 4) % 4;
        const nextIndex = (index + 1) % 4;

        newPoints[index] = currentPoint;
        newPoints[prevIndex] = [currentPoint[0], newPoints[prevIndex][1]];
        newPoints[nextIndex] = [newPoints[nextIndex][0], currentPoint[1]];
      } else {
        // Edge point
        const prevIndex = (index - 1 + 4) % 4;
        const nextIndex = (index + 1) % 4;

        newPoints[index] = currentPoint;
        newPoints[prevIndex] = [newPoints[prevIndex][0], currentPoint[1]];
        newPoints[nextIndex] = [currentPoint[0], newPoints[nextIndex][1]];
      }

      // Ensure all points are within bounds
      newPoints = newPoints.map(point => [
        Math.min(Math.max(point[0], OFFSET), size.width - OFFSET),
        Math.min(Math.max(point[1], OFFSET), size.height - OFFSET)
      ]);
    } else {
      // For polygons, just update the dragged point
      newPoints = currentPolygon.points.map((point, idx) =>
        idx === data.index ? constrainedPoint : point
      );
    }

    const newAnnotations = annotationData.map((poly, index) =>
      index === activePolygonIndex
        ? {
            ...poly,
            points: newPoints
          }
        : poly
    );

    dispatch(setAnnotationData(newAnnotations));
  };

  const handlePolygonClick = index => {
    if (mode !== MODES.DRAG) return;
    setActivePolygonIndex(activePolygonIndex === index ? -1 : index);
  };

  const handleMouseOverStartPoint = e => {
    if (isPolyComplete || points.length < 3) return;
    e.target.scale({ x: 3, y: 3 });
    setMouseOverPoint(true);
  };

  const handleMouseOutStartPoint = e => {
    e.target.scale({ x: 1, y: 1 });
    setMouseOverPoint(false);
  };

  return (
    <div style={wrapperStyle}>
      <div style={columnStyle} ref={canvasWrapperRef}>
        {loading ? (
          <div
            style={{
              height: 540,
              width: 960,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <LoadingBox />
          </div>
        ) : (
          <Stage
            ref={stageRef}
            width={size.width || 650}
            height={size.height || 302}
            onMouseMove={handleMouseMove}
            onMouseDown={handleMouseDown}
          >
            <Layer>
              {!isLoading && image && (
                <Image
                  ref={imageRef}
                  image={image}
                  x={0}
                  y={0}
                  width={size.width}
                  height={size.height}
                />
              )}
              {!isLoading && image && (
                <>
                  {/* First render non-selected polygons */}
                  {annotationData.map((shape, index) =>
                    index !== activePolygonIndex ? (
                      <PolygonAnnotation
                        size={size}
                        key={shape.id}
                        points={shape.points}
                        flattenedPoints={shape.points.reduce(
                          (a, b) => a.concat(b),
                          []
                        )}
                        handlePointDragMove={e => handlePointDragMove(e, index)}
                        handleMouseOverStartPoint={handleMouseOverStartPoint}
                        handleMouseOutStartPoint={handleMouseOutStartPoint}
                        isFinished={true}
                        isSelected={false}
                        onClick={() => handlePolygonClick(index)}
                        label={shape.label}
                        isDragMode={mode === MODES.DRAG}
                        color={shape.color}
                      />
                    ) : null
                  )}

                  {/* Then render the currently drawing polygon */}
                  {isDrawing && points.length > 0 && (
                    <>
                      <KonvaLine
                        points={getCurrentLinePoints()}
                        stroke="#00ff00"
                        strokeWidth={2}
                        dash={[5, 5]}
                        closed={mode === MODES.DRAW_RECTANGLE || isPolyComplete}
                      />
                      <PolygonAnnotation
                        points={points}
                        flattenedPoints={flattenedPoints}
                        handlePointDragMove={handlePointDragMove}
                        handleMouseOverStartPoint={handleMouseOverStartPoint}
                        handleMouseOutStartPoint={handleMouseOutStartPoint}
                        isFinished={isPolyComplete}
                        isSelected={true}
                        isDragMode={mode === MODES.DRAG}
                        color={{ stroke: '#00ff00', fill: 'rgba(0,255,0,0.3)' }}
                      />
                    </>
                  )}

                  {/* Finally render the selected polygon on top */}
                  {activePolygonIndex !== -1 &&
                    annotationData.map((shape, index) =>
                      index === activePolygonIndex ? (
                        <PolygonAnnotation
                          key={`selected-${shape.id}`}
                          points={shape.points}
                          flattenedPoints={shape.points.reduce(
                            (a, b) => a.concat(b),
                            []
                          )}
                          handlePointDragMove={e =>
                            handlePointDragMove(e, index)
                          }
                          handleMouseOverStartPoint={handleMouseOverStartPoint}
                          handleMouseOutStartPoint={handleMouseOutStartPoint}
                          isFinished={true}
                          isSelected={true}
                          onClick={() => handlePolygonClick(index)}
                          label={shape.label}
                          isDragMode={mode === MODES.DRAG}
                          color={shape.color}
                        />
                      ) : null
                    )}
                </>
              )}
            </Layer>
          </Stage>
        )}
        {labelInput.visible && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 100000 // This div should have pointer-events: none
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: labelInput.x + 90,
                top: labelInput.y,
                pointerEvents: 'auto',
                transform: 'translate(-50%, -50%)'
              }}
            >
              <LabelSuggestionInput
                points={points}
                existingLabels={getExistingLabels?.data}
                annotatedList={annotationData}
                onSubmit={handleLabelSubmit}
                position={{ x: 0, y: 0 }} // Changed to 0,0 since we're positioning the parent div
                onCancel={() =>
                  setLabelInput({ visible: false, x: 0, y: 0, value: '' })
                }
              />
            </div>
          </div>
        )}
      </div>
      <div className={Styles.annotationoptionsholder}>
        <div className={Styles.iconsholder}>
          <Tooltip title="Draw Rectangle (R)" placement="top-start">
            <img
              src={PolygonIcon}
              className={
                mode === MODES.DRAW_RECTANGLE
                  ? Styles.actioniconactive
                  : Styles.actionicon
              }
              alt="action_icon"
              onClick={() => handleModeChange(MODES.DRAW_RECTANGLE)}
            />
          </Tooltip>
          <Tooltip title="Draw Polygon (P)" placement="top-start">
            <img
              src={RoiIcon}
              className={
                mode === MODES.DRAW_POLYGON
                  ? Styles.actioniconactive
                  : Styles.actionicon
              }
              alt="action_icon"
              onClick={() => handleModeChange(MODES.DRAW_POLYGON)}
            />
          </Tooltip>
          <Tooltip title="Replicate" placement="top-start">
            <Copy
              src={RoiIcon}
              className={`${Styles.actionicon} 
                ${
                  annotationData.length > 0 && frameIndex < 500
                    ? ''
                    : Styles.disabled
                }
              `}
              alt="replicate_icon"
              onClick={
                annotationData.length > 0 &&
                frameIndex < 500 &&
                handleReplicateFrameData
              }
            />
          </Tooltip>
        </div>
        <div className={Styles.divider}></div>
        <div className={Styles.iconsholder}>
          <Tooltip title="Undo (Ctrl+Z)" placement="top-start">
            <img
              src={UndoIcon}
              className={`${Styles.actionicon} ${
                past.length === 0 || isDrawingInProgress ? Styles.disabled : ''
              }`}
              alt="action_icon"
              onClick={() => {
                if (!isDrawingInProgress && past.length > 0) {
                  handleUndo();
                }
              }}
            />
          </Tooltip>
          <Tooltip title="Redo (Ctrl+Y)" placement="top-start">
            <img
              src={RedoIcon}
              className={`${Styles.actionicon} ${
                future.length === 0 || isDrawingInProgress
                  ? Styles.disabled
                  : ''
              }`}
              alt="action_icon"
              onClick={() => {
                if (!isDrawingInProgress && future.length > 0) {
                  handleRedo();
                }
              }}
            />
          </Tooltip>
        </div>
        <div className={Styles.divider}></div>
        <div className={Styles.iconsholder}>
          <Tooltip title="Grab/Edit (D)" placement="top-start">
            <img
              onClick={() => handleModeChange(MODES.DRAG)}
              src={HandCursorIcon}
              className={
                mode === MODES.DRAG
                  ? Styles.actioniconactive
                  : Styles.actionicon
              }
              alt="action_icon"
            />
          </Tooltip>
          <Tooltip title="Delete (Del)" placement="top-start">
            <Trash2
              onClick={() =>
                activePolygonIndex !== -1 &&
                handleDeletePolygon(activePolygonIndex)
              }
              style={{ opacity: activePolygonIndex !== -1 ? 1 : 0.5 }}
            />
          </Tooltip>
          <Tooltip title="Reset" placement="top-start">
            <RotateCcw
              onClick={handleReset}
              className={Styles.actionicon}
              style={{
                opacity: annotationData.length > 0 ? 1 : 0.5,
                cursor: 'pointer'
              }}
            />
          </Tooltip>
          {/* <img
            src={ZoomInIcon}
            className={Styles.actionicon}
            alt="action_icon"
          />
          <img
            src={ZoomOutIcon}
            className={Styles.actionicon}
            alt="action_icon"
          /> */}
        </div>
      </div>
    </div>
  );
};

export default Canvas;
